/**
 * Ported VERBATIM from apr_template `src/pages/ApplicationDependencies.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect } from 'react';
import { authFetch } from '../compat/api';
import './ApplicationDependencies.css';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import { dispositionColor as matrixColor } from '../dispositionPalette';
import { DispositionChip } from '../compat/DispositionChip';
import type { Help } from '../compat/helpTypes';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, panels and token pairs.
import { Icon, IconButton, Sheet, icons } from '@platform/ui-kit/workspace';
import { WS_CHIP, WS_COLOR, WS_RADIUS, WS_STATUS, wsTone, wsType } from '../workspace/ws';
import { GroupMultiSelect, ALL_GROUPS_LABEL, inPicked } from '../workspace/GroupMultiSelect';
import { useWsDialogs } from '../workspace/useWsDialogs';

const ApplicationDependenciesHelp: Help = {
  title: 'Application Dependencies',
  icon: '🔗',
  purpose:
    'Capture the sequencing relationships between applications within an integration group, so the roadmap schedules them in the right order.',
  doHere: [
    'Click <strong>+ Add Dependency</strong>, pick an <strong>Integration Group</strong>, then a <strong>Source</strong> and <strong>Target</strong> application.',
    'Choose a <strong>Dependency Type</strong>: Sequential (Finish-to-Start) or Overlapping Phases.',
    'For overlapping phases, set the source phase at which the target may start.',
    'Filter the table by source or target app; use <strong>&#128065;</strong> / <strong>&#9999;&#65039;</strong> / <strong>&#128465;&#65039;</strong> to view, edit or delete.',
    'The system blocks duplicate and reverse (circular) dependencies.',
  ],
  fits: 'Integration &rarr; Grouping &amp; Scope &rarr; Application Dependencies',
  firstTime:
    'Add one dependency: select a group, a source and target app, keep Finish-to-Start, and save.',
  sections: [
    {
      type: 'defs',
      label: 'Dependency types',
      items: [
        { term: 'Finish&rarr;Start', color: '#1565c0', def: 'Target application starts only after the source application fully completes.' },
        { term: 'Overlap', color: '#e65100', def: 'Applications run in parallel; the target starts once the source reaches a chosen phase.' },
      ],
    },
  ],
  links: [{ href: '/integration-framework/integration-groupings', label: 'Integration Groupings' }],
};

// Types
interface ApplicationDependency {
  dependency_id: number;
  integration_group_id: string;
  /** Owner request 2026-11-04 (migration 0155): the target app's group — may differ from the source group. */
  target_integration_group_id?: string;
  target_integration_group_name?: string;
  /** Owner ruling 2026-11-04: a same-group dependency of an approved roadmap is read-only. */
  locked?: boolean;
  source_application_id: string;
  target_application_id: string;
  source_application_name?: string;
  target_application_name?: string;
  source_disposition?: string;
  target_disposition?: string;
  integration_group_name?: string;
  dependency_type: 'finish_to_start' | 'phase_based';
  source_phase_name?: string;
  target_phase_name?: string;
  overlap_days?: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

interface IntegrationGroup {
  integration_group_id: string;
  integration_group_description: string;
  application_count: number;
  /** The grouping's roadmap has been confirmed (owner ruling 2026-11-04). */
  roadmap_approved?: boolean;
}

interface Application {
  application_id: string;
  description: string;
  disposition?: string;
}

// Helper function to get disposition badge color (consistent with table display)
// PORT NOTE: a disposition badge is a FILLED chip in the Disposition Matrix colour, with
// readable text on it (§1.9.3 rules 2-3) — not the source's hard-coded wash.
const getDispositionColor = (disposition?: string) => {
  const known = matrixColor(disposition);
  // DW: "named by the matrix" = not the matrix's own neutral fallback; unnamed → kit neutral pair.
  return disposition && known.base !== matrixColor(undefined).base ? { bg: known.base, color: known.onBase } : { bg: WS_STATUS.neutral.fill, color: WS_STATUS.neutral.ink };
};

// Owner request 2026-11-04: the table's app IDs are black with the disposition chip after them
// (compat/DispositionChip, as on Roadmap Visualization) instead of ID text in the matrix ink.

// Valid phases
const PHASES = [
  'Assessment & Planning',
  'Design & Architecture',
  'Migration Execution',
  'Testing & Validation',
  'Go-Live & Optimization'
];

const ApplicationDependencies: React.FC = () => {
  const { confirm, notify, dialogs } = useWsDialogs();
  // State
  const [dependencies, setDependencies] = useState<ApplicationDependency[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filterGroupIds, setFilterGroupIds] = useState<string[]>([]);
  const [filterSourceApp, setFilterSourceApp] = useState('');
  const [filterTargetApp, setFilterTargetApp] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit' | 'view'>('add');
  const [editingDependency, setEditingDependency] = useState<ApplicationDependency | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    integration_group_id: '',
    target_integration_group_id: '',
    source_application_id: '',
    target_application_id: '',
    dependency_type: 'finish_to_start' as 'finish_to_start' | 'phase_based',
    source_phase_name: 'Assessment & Planning',
    target_phase_name: 'Assessment & Planning',
    notes: ''
  });

  // Dropdown state
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [showTargetDropdown, setShowTargetDropdown] = useState(false);
  const [showTargetGroupDropdown, setShowTargetGroupDropdown] = useState(false);
  // The target group's applications (the source group's are `applications`).
  const [targetApplications, setTargetApplications] = useState<Application[]>([]);

  // Load data on mount
  useEffect(() => {
    loadDependencies();
    loadIntegrationGroups();
  }, []);

  // Load applications when integration group changes
  useEffect(() => {
    if (formData.integration_group_id) {
      loadApplicationsForGroup(formData.integration_group_id);
    } else {
      setApplications([]);
    }
  }, [formData.integration_group_id]);

  // Load the target group's applications when the target group changes
  useEffect(() => {
    const g = formData.target_integration_group_id;
    if (!g) { setTargetApplications([]); return; }
    authFetch(`/api/application-dependencies/lookup/applications/${g}`)
      .then((r) => r.json())
      .then((j) => setTargetApplications(j.success ? (j.data || []) : []))
      .catch(() => setTargetApplications([]));
  }, [formData.target_integration_group_id]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.custom-dropdown')) {
        setShowGroupDropdown(false);
        setShowSourceDropdown(false);
        setShowTargetDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadDependencies = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/application-dependencies');
      const result = await response.json();
      if (result.success) {
        setDependencies(result.data || []);
      } else {
        setError(result.error || 'Failed to load dependencies');
      }
    } catch (err) {
      console.error('Error loading dependencies:', err);
      setError('Error loading dependencies');
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/application-dependencies/lookup/integration-groups');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      }
    } catch (err) {
      console.error('Error loading integration groups:', err);
    }
  };

  const loadApplicationsForGroup = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setApplications(result.data || []);
      }
    } catch (err) {
      console.error('Error loading applications:', err);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setEditingDependency(null);
    setFormData({
      integration_group_id: '',
      target_integration_group_id: '',
      source_application_id: '',
      target_application_id: '',
      dependency_type: 'finish_to_start',
      source_phase_name: 'Assessment & Planning',
      target_phase_name: 'Assessment & Planning',
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (dep: ApplicationDependency) => {
    setModalMode('edit');
    setEditingDependency(dep);
    setFormData({
      integration_group_id: dep.integration_group_id,
      target_integration_group_id: dep.target_integration_group_id || dep.integration_group_id,
      source_application_id: dep.source_application_id,
      target_application_id: dep.target_application_id,
      dependency_type: dep.dependency_type,
      source_phase_name: dep.source_phase_name || 'Assessment & Planning',
      target_phase_name: dep.target_phase_name || 'Assessment & Planning',
      notes: dep.notes || ''
    });
    setShowModal(true);
  };

  const openViewModal = (dep: ApplicationDependency) => {
    setModalMode('view');
    setEditingDependency(dep);
    setFormData({
      integration_group_id: dep.integration_group_id,
      target_integration_group_id: dep.target_integration_group_id || dep.integration_group_id,
      source_application_id: dep.source_application_id,
      target_application_id: dep.target_application_id,
      dependency_type: dep.dependency_type,
      source_phase_name: dep.source_phase_name || 'Assessment & Planning',
      target_phase_name: dep.target_phase_name || 'Assessment & Planning',
      notes: dep.notes || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDependency(null);
    setShowGroupDropdown(false);
    setShowSourceDropdown(false);
    setShowTargetDropdown(false);
    setShowTargetGroupDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.integration_group_id || !formData.target_integration_group_id || !formData.source_application_id || !formData.target_application_id) {
      notify('Please fill in all required fields', 'error');
      return;
    }

    if (sameGroupApproved) {
      notify(`The ${formData.integration_group_id} roadmap is approved: dependencies within ${formData.integration_group_id} can no longer be changed. Choose a different Target Integration Group.`, 'error');
      return;
    }

    if (formData.source_application_id === formData.target_application_id && formData.integration_group_id === formData.target_integration_group_id) {
      notify('The same application cannot depend on itself within one integration group. Choose a different Target Integration Group.', 'error');
      return;
    }

    // Check for duplicate dependency (only for add mode)
    if (modalMode === 'add') {
      const tg = (dep: ApplicationDependency) => dep.target_integration_group_id || dep.integration_group_id;
      const existingDuplicate = dependencies.find(dep =>
        dep.source_application_id === formData.source_application_id &&
        dep.target_application_id === formData.target_application_id &&
        dep.integration_group_id === formData.integration_group_id &&
        tg(dep) === formData.target_integration_group_id
      );
      if (existingDuplicate) {
        notify(`A dependency already exists from ${formData.source_application_id} to ${formData.target_application_id}. Each Source→Target combination can only exist once.`, 'error');
        return;
      }

      // Check for circular dependency
      const existingReverse = dependencies.find(dep =>
        dep.source_application_id === formData.target_application_id &&
        dep.target_application_id === formData.source_application_id &&
        dep.integration_group_id === formData.target_integration_group_id &&
        tg(dep) === formData.integration_group_id
      );
      if (existingReverse) {
        notify(`Cannot create dependency: A reverse dependency already exists (${formData.target_application_id} → ${formData.source_application_id}). This would create a circular dependency loop.`, 'error');
        return;
      }
    }

    try {
      const payload = {
        integration_group_id: formData.integration_group_id,
        target_integration_group_id: formData.target_integration_group_id,
        source_application_id: formData.source_application_id,
        target_application_id: formData.target_application_id,
        dependency_type: formData.dependency_type,
        source_phase_name: formData.dependency_type === 'phase_based' ? formData.source_phase_name : null,
        target_phase_name: formData.dependency_type === 'phase_based' ? formData.target_phase_name : null,
        notes: formData.notes || null
      };

      const url = modalMode === 'edit' && editingDependency
        ? `/api/application-dependencies/${editingDependency.dependency_id}`
        : '/api/application-dependencies';

      const method = modalMode === 'edit' ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        notify(result.message || 'Dependency saved successfully', 'success');
        closeModal();
        loadDependencies();
      } else {
        notify(result.error || 'Failed to save dependency', 'error');
      }
    } catch (err) {
      console.error('Error saving dependency:', err);
      notify('Error saving dependency', 'error');
    }
  };

  const handleDelete = async (dep: ApplicationDependency) => {
    if (!(await confirm(`Are you sure you want to delete this dependency between ${dep.source_application_id} and ${dep.target_application_id}?`, { tone: 'destructive' }))) {
      return;
    }

    try {
      const response = await authFetch(`/api/application-dependencies/${dep.dependency_id}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        loadDependencies();
      } else {
        notify(result.error || 'Failed to delete dependency', 'error');
      }
    } catch (err) {
      console.error('Error deleting dependency:', err);
      notify('Error deleting dependency', 'error');
    }
  };

  const getRelationshipDisplay = (dep: ApplicationDependency) => {
    if (dep.dependency_type === 'finish_to_start') {
      return 'Finish→Start';
    }
    // For phase_based, show phase abbreviations
    const sourcePhase = dep.source_phase_name || 'Assessment & Planning';
    const targetPhase = dep.target_phase_name || 'Assessment & Planning';

    const getPhaseAbbrev = (phase: string) => {
      switch (phase) {
        case 'Assessment & Planning': return 'Assess';
        case 'Design & Architecture': return 'Design';
        case 'Migration Execution': return 'Migrate';
        case 'Testing & Validation': return 'Test';
        case 'Go-Live & Optimization': return 'Go-Live';
        default: return phase.substring(0, 6);
      }
    };

    return `${getPhaseAbbrev(sourcePhase)}→${getPhaseAbbrev(targetPhase)}`;
  };

  const filteredDependencies = dependencies.filter(dep => {
    const sourceMatch = !filterSourceApp ||
      dep.source_application_id.toLowerCase().includes(filterSourceApp.toLowerCase()) ||
      (dep.source_application_name || '').toLowerCase().includes(filterSourceApp.toLowerCase());
    const targetMatch = !filterTargetApp ||
      dep.target_application_id.toLowerCase().includes(filterTargetApp.toLowerCase()) ||
      (dep.target_application_name || '').toLowerCase().includes(filterTargetApp.toLowerCase());
    const groupMatch = inPicked(filterGroupIds, dep.integration_group_id) || inPicked(filterGroupIds, dep.target_integration_group_id || dep.integration_group_id);
    return groupMatch && sourceMatch && targetMatch;
  });

  const selectedGroup = integrationGroups.find(g => g.integration_group_id === formData.integration_group_id);
  const selectedSourceApp = applications.find(a => a.application_id === formData.source_application_id);
  const selectedTargetApp = targetApplications.find(a => a.application_id === formData.target_application_id);
  const selectedTargetGroup = integrationGroups.find(g => g.integration_group_id === formData.target_integration_group_id);
  // Owner ruling 2026-11-04: an approved roadmap freezes the dependencies inside its own grouping.
  const sameGroupApproved = modalMode === 'add' && !!formData.integration_group_id &&
    formData.integration_group_id === formData.target_integration_group_id &&
    !!integrationGroups.find(g => g.integration_group_id === formData.integration_group_id)?.roadmap_approved;

  const modalTitle = modalMode === 'add' ? 'Add Dependency Relationship' :
    modalMode === 'edit' ? 'Edit Dependency Relationship' :
    'View Dependency Details';

  return (
    <div className="page">
      <HelpPanel help={ApplicationDependenciesHelp} />
      <PageHeader
        title="Application Interdependencies"
        subtitle="Manage dependencies and relationships between applications"
        actions={<>
          <button className="btn btn-primary" onClick={openAddModal}>
            <Icon icon={icons.Plus} size={20} /> Add Dependency
          </button>
        </>}
      />

      {/* Main Content */}
      <div className="master-data-container">
        {/* Filter Section */}
        {dependencies.length > 0 && (
          <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {/* Integration Group filter: the same control as Roadmap Schedule's selector. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GroupMultiSelect
                  testId="dep-group-filter"
                  label="Integration Group:"
                  placeholder={ALL_GROUPS_LABEL}
                  groups={integrationGroups.map(g => ({ id: g.integration_group_id, description: g.integration_group_description }))}
                  value={filterGroupIds}
                  onChange={setFilterGroupIds}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ ...wsType('label'), minWidth: '100px' }}>Source App:</label>
                <input
                  type="text"
                  value={filterSourceApp}
                  onChange={(e) => setFilterSourceApp(e.target.value)}
                  placeholder="Filter by source..."
                  style={{ minWidth: '180px' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ ...wsType('label'), minWidth: '100px' }}>Target App:</label>
                <input
                  type="text"
                  value={filterTargetApp}
                  onChange={(e) => setFilterTargetApp(e.target.value)}
                  placeholder="Filter by target..."
                  style={{ minWidth: '180px' }}
                />
              </div>
              {(filterGroupIds.length > 0 || filterSourceApp || filterTargetApp) && (
                <button
                  className="btn"
                  onClick={() => { setFilterGroupIds([]); setFilterSourceApp(''); setFilterTargetApp(''); }}
                >
                  Clear Filters
                </button>
              )}
              <span style={{ ...wsType('label'), color: WS_COLOR.textMuted, marginLeft: 'auto' }}>
                {filteredDependencies.length} dependenc{filteredDependencies.length === 1 ? 'y' : 'ies'}
              </span>
            </div>
          </div>
        )}

        {/* Dependencies Table */}
        <div className="md-card">
          {loading ? (
            <div className="md-loading-state">
              <div className="md-spinner"></div>
              <p>Loading dependencies...</p>
            </div>
          ) : error ? (
            <div className="md-error-state">
              <div className="md-error-icon"><Icon icon={icons.AlertTriangle} size={24} /></div>
              <h3>Error Loading Data</h3>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={loadDependencies}>Retry</button>
            </div>
          ) : dependencies.length === 0 ? (
            <div className="md-empty-state">
              <div className="md-empty-icon"><Icon icon={icons.Link2} size={24} /></div>
              <h3>No Dependencies Found</h3>
              <p>Create dependencies between applications to establish relationships</p>
              <button className="btn btn-primary" onClick={openAddModal} style={{ marginTop: '16px' }}>
                <Icon icon={icons.Plus} size={20} /> Add Dependency
              </button>
            </div>
          ) : (
            <div className="md-table-container">
              <table className="md-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'center', minWidth: '200px' }}>Source Grouping ID</th>
                    <th style={{ textAlign: 'center' }}>Source App</th>
                    <th style={{ textAlign: 'left' }}>Description</th>
                    <th style={{ textAlign: 'center' }}>Relationship</th>
                    <th style={{ textAlign: 'center', minWidth: '160px' }}>Target Grouping ID</th>
                    <th style={{ textAlign: 'center' }}>Target App</th>
                    <th style={{ textAlign: 'left' }}>Description</th>
                    <th style={{ textAlign: 'center' }}>Overlap</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDependencies.map((dep) => (
                    <tr key={dep.dependency_id}>
                      {/* Grouping ID — as Roadmap Schedule shows it: the ID, its name beneath. */}
                      <td style={{ textAlign: 'left' }} data-testid="dep-group-cell">
                        <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text, marginBottom: '4px' }}>
                          {dep.integration_group_id}
                        </div>
                        <div style={{ ...wsType('table'), color: WS_COLOR.textMuted }}>
                          {dep.integration_group_name || '-'}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                          <strong style={{ color: WS_COLOR.text }}>{dep.source_application_id}</strong>
                          <DispositionChip value={dep.source_disposition} />
                        </span>
                      </td>
                      <td style={{ textAlign: 'left', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: WS_COLOR.text }}>
                        {dep.source_application_name || '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ ...WS_CHIP, ...wsTone(dep.dependency_type === 'finish_to_start' ? 'info' : 'warning') }}>
                          {getRelationshipDisplay(dep)}
                        </span>
                      </td>
                      {/* Target Grouping ID — owner request 2026-11-04 (migration 0155) */}
                      <td style={{ textAlign: 'left' }} data-testid="dep-target-group-cell">
                        <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text, marginBottom: '4px' }}>
                          {dep.target_integration_group_id || dep.integration_group_id}
                        </div>
                        <div style={{ ...wsType('table'), color: WS_COLOR.textMuted }}>
                          {dep.target_integration_group_name || dep.integration_group_name || '-'}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                          <strong style={{ color: WS_COLOR.text }}>{dep.target_application_id}</strong>
                          <DispositionChip value={dep.target_disposition} />
                        </span>
                      </td>
                      <td style={{ textAlign: 'left', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: WS_COLOR.text }}>
                        {dep.target_application_name || '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ ...WS_CHIP, ...wsTone(dep.dependency_type === 'finish_to_start' ? 'success' : 'warning') }}>
                          {dep.dependency_type === 'finish_to_start' ? 'No Overlap' : 'Overlap'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                          <IconButton
                            icon={icons.Eye}
                            label="View details"
                            title="View details"
                            onClick={() => openViewModal(dep)}
                          />
                          {dep.locked ? (
                            <span
                              data-testid="dep-locked"
                              style={{ ...WS_CHIP, ...wsTone('info'), whiteSpace: 'nowrap' }}
                              title={`The ${dep.integration_group_id} roadmap is approved: this dependency can no longer be changed`}
                            >
                              <Icon icon={icons.Lock} size={16} /> Approved
                            </span>
                          ) : (
                            <>
                              <IconButton
                                icon={icons.Pencil}
                                label="Edit dependency"
                                title="Edit dependency"
                                onClick={() => openEditModal(dep)}
                              />
                              <IconButton
                                icon={icons.Trash2}
                                label="Delete dependency"
                                title="Delete dependency"
                                onClick={() => handleDelete(dep)}
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit/View — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showModal}
        title={modalTitle}
        onClose={closeModal}
        width={531}
        actions={
          <>
            <button type="button" className="btn" onClick={closeModal}>
              {modalMode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {modalMode !== 'view' && (
              <button type="submit" form="appdep-form" className="btn btn-primary">
                {modalMode === 'edit' ? 'Update Dependency' : 'Save Dependency'}
              </button>
            )}
          </>
        }
      >
            <form id="appdep-form" onSubmit={handleSubmit}>
              {/* Integration Group */}
              <div style={{ marginBottom: '20px' }}>
                <label style={FIELD_LABEL}>
                  Source Integration Group *
                </label>
                <div className="custom-dropdown" style={{ position: 'relative', backgroundColor: modalMode !== 'add' ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}>
                  <div
                    onClick={() => {
                      if (modalMode === 'add') {
                        setShowGroupDropdown(!showGroupDropdown);
                        setShowSourceDropdown(false);
                        setShowTargetDropdown(false);
                      }
                    }}
                    style={{
                      ...TRIGGER,
                      cursor: modalMode === 'add' ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <span style={{ color: selectedGroup ? WS_COLOR.text : WS_COLOR.textMuted }}>
                      {selectedGroup
                        ? `${selectedGroup.integration_group_id} - ${selectedGroup.integration_group_description} (${selectedGroup.application_count} apps)`
                        : 'Select the Source Integration Group'}
                    </span>
                    {modalMode === 'add' && <Icon icon={icons.ChevronDown} size={16} />}
                  </div>
                  {showGroupDropdown && modalMode === 'add' && (
                    <div style={MENU}>
                      {integrationGroups.length === 0 ? (
                        <div style={{ padding: '12px 16px', color: WS_COLOR.textMuted, ...wsType('table') }}>
                          No integration groups available
                        </div>
                      ) : (
                        integrationGroups.map(group => (
                          <div
                            key={group.integration_group_id}
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                integration_group_id: group.integration_group_id,
                                // the target group follows the source group until it is chosen separately
                                target_integration_group_id: !prev.target_integration_group_id || prev.target_integration_group_id === prev.integration_group_id ? group.integration_group_id : prev.target_integration_group_id,
                                source_application_id: '',
                                target_application_id: ''
                              }));
                              setShowGroupDropdown(false);
                            }}
                            style={{
                              ...OPTION,
                              display: 'block',
                              backgroundColor: formData.integration_group_id === group.integration_group_id ? WS_STATUS.info.fill : WS_COLOR.surface
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = WS_COLOR.surfaceSubtle)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.integration_group_id === group.integration_group_id ? WS_STATUS.info.fill : WS_COLOR.surface)}
                          >
                            <strong>{group.integration_group_id}</strong> - {group.integration_group_description}
                            <span style={{ float: 'right', color: WS_COLOR.textMuted }}>({group.application_count} apps)</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Source Application */}
              <div style={{ marginBottom: '20px' }}>
                <label style={FIELD_LABEL}>
                  Source Application *
                </label>
                <div className="custom-dropdown" style={{ position: 'relative', backgroundColor: modalMode !== 'add' || !formData.integration_group_id ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}>
                  <div
                    onClick={() => {
                      if (modalMode === 'add' && formData.integration_group_id) {
                        setShowSourceDropdown(!showSourceDropdown);
                        setShowGroupDropdown(false);
                        setShowTargetDropdown(false);
                      }
                    }}
                    style={{
                      ...TRIGGER,
                      cursor: modalMode === 'add' && formData.integration_group_id ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <span style={{ color: selectedSourceApp ? WS_COLOR.text : WS_COLOR.textMuted, display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      {selectedSourceApp
                        ? (
                          <>
                            <span>{selectedSourceApp.application_id} - {selectedSourceApp.description}</span>
                            {selectedSourceApp.disposition && (
                              <span style={{
                                ...WS_CHIP,
                                backgroundColor: getDispositionColor(selectedSourceApp.disposition).bg,
                                color: getDispositionColor(selectedSourceApp.disposition).color
                              }}>
                                {selectedSourceApp.disposition}
                              </span>
                            )}
                          </>
                        )
                        : (formData.integration_group_id ? 'Select Application' : 'Select Integration Group first')}
                    </span>
                    {modalMode === 'add' && formData.integration_group_id && <Icon icon={icons.ChevronDown} size={16} />}
                  </div>
                  {showSourceDropdown && modalMode === 'add' && (
                    <div style={MENU}>
                      {applications.filter(a => a.application_id !== formData.target_application_id).map(app => (
                        <div
                          key={app.application_id}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, source_application_id: app.application_id }));
                            setShowSourceDropdown(false);
                          }}
                          style={{
                            ...OPTION,
                            backgroundColor: formData.source_application_id === app.application_id ? WS_STATUS.info.fill : WS_COLOR.surface
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = WS_COLOR.surfaceSubtle)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.source_application_id === app.application_id ? WS_STATUS.info.fill : WS_COLOR.surface)}
                        >
                          <span><strong>{app.application_id}</strong> - {app.description}</span>
                          {app.disposition && (
                            <span style={{
                              ...WS_CHIP,
                              backgroundColor: getDispositionColor(app.disposition).bg,
                              color: getDispositionColor(app.disposition).color,
                              marginLeft: '8px'
                            }}>
                              {app.disposition}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Target Integration Group — owner request 2026-11-04 (migration 0155) */}
              <div style={{ marginBottom: '20px' }} data-testid="target-group-field">
                <label style={FIELD_LABEL}>
                  Target Integration Group *
                </label>
                <div className="custom-dropdown" style={{ position: 'relative', backgroundColor: modalMode !== 'add' ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}>
                  <div
                    onClick={() => {
                      if (modalMode === 'add') {
                        setShowTargetGroupDropdown(!showTargetGroupDropdown);
                        setShowGroupDropdown(false);
                        setShowSourceDropdown(false);
                        setShowTargetDropdown(false);
                      }
                    }}
                    style={{ ...TRIGGER, cursor: modalMode === 'add' ? 'pointer' : 'not-allowed' }}
                  >
                    <span style={{ color: selectedTargetGroup ? WS_COLOR.text : WS_COLOR.textMuted }}>
                      {selectedTargetGroup
                        ? `${selectedTargetGroup.integration_group_id} - ${selectedTargetGroup.integration_group_description} (${selectedTargetGroup.application_count} apps)`
                        : 'Select the Target Integration Group'}
                    </span>
                    {modalMode === 'add' && <Icon icon={icons.ChevronDown} size={16} />}
                  </div>
                  {sameGroupApproved && !showTargetGroupDropdown && (
                    <div data-testid="target-group-approved-note" style={{ ...wsType('meta'), color: WS_STATUS.warning.ink, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon icon={icons.Lock} size={16} />
                      The {formData.integration_group_id} roadmap is approved — choose a different Target Integration Group.
                    </div>
                  )}
                  {showTargetGroupDropdown && modalMode === 'add' && (
                    <div style={MENU}>
                      {integrationGroups.map(group => (
                        <div
                          key={group.integration_group_id}
                          data-testid="target-group-option"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, target_integration_group_id: group.integration_group_id, target_application_id: '' }));
                            setShowTargetGroupDropdown(false);
                          }}
                          style={{
                            ...OPTION,
                            display: 'block',
                            backgroundColor: formData.target_integration_group_id === group.integration_group_id ? WS_STATUS.info.fill : WS_COLOR.surface
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = WS_COLOR.surfaceSubtle)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.target_integration_group_id === group.integration_group_id ? WS_STATUS.info.fill : WS_COLOR.surface)}
                        >
                          <strong>{group.integration_group_id}</strong> - {group.integration_group_description}
                          <span style={{ float: 'right', color: WS_COLOR.textMuted }}>({group.application_count} apps)</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Target Application */}
              <div style={{ marginBottom: '20px' }}>
                <label style={FIELD_LABEL}>
                  Target Application *
                </label>
                <div className="custom-dropdown" style={{ position: 'relative', backgroundColor: modalMode !== 'add' || !formData.target_integration_group_id ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}>
                  <div
                    onClick={() => {
                      if (modalMode === 'add' && formData.target_integration_group_id) {
                        setShowTargetDropdown(!showTargetDropdown);
                        setShowGroupDropdown(false);
                        setShowSourceDropdown(false);
                      }
                    }}
                    style={{
                      ...TRIGGER,
                      cursor: modalMode === 'add' && formData.target_integration_group_id ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <span style={{ color: selectedTargetApp ? WS_COLOR.text : WS_COLOR.textMuted, display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      {selectedTargetApp
                        ? (
                          <>
                            <span>{selectedTargetApp.application_id} - {selectedTargetApp.description}</span>
                            {selectedTargetApp.disposition && (
                              <span style={{
                                ...WS_CHIP,
                                backgroundColor: getDispositionColor(selectedTargetApp.disposition).bg,
                                color: getDispositionColor(selectedTargetApp.disposition).color
                              }}>
                                {selectedTargetApp.disposition}
                              </span>
                            )}
                          </>
                        )
                        : (formData.target_integration_group_id ? 'Select Application' : 'Select Target Integration Group first')}
                    </span>
                    {modalMode === 'add' && formData.target_integration_group_id && <Icon icon={icons.ChevronDown} size={16} />}
                  </div>
                  {showTargetDropdown && modalMode === 'add' && (
                    <div style={MENU}>
                      {/* Owner request 2026-11-04: every app of the target group is listed, the source app included,
                          so the same app can be targeted in another group. */}
                      {targetApplications.map(app => (
                        <div
                          key={app.application_id}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, target_application_id: app.application_id }));
                            setShowTargetDropdown(false);
                          }}
                          style={{
                            ...OPTION,
                            backgroundColor: formData.target_application_id === app.application_id ? WS_STATUS.info.fill : WS_COLOR.surface
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = WS_COLOR.surfaceSubtle)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.target_application_id === app.application_id ? WS_STATUS.info.fill : WS_COLOR.surface)}
                        >
                          <span><strong>{app.application_id}</strong> - {app.description}</span>
                          {app.disposition && (
                            <span style={{
                              ...WS_CHIP,
                              backgroundColor: getDispositionColor(app.disposition).bg,
                              color: getDispositionColor(app.disposition).color,
                              marginLeft: '8px'
                            }}>
                              {app.disposition}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Dependency Type */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...FIELD_LABEL, marginBottom: '12px' }}>
                  Dependency Type *
                </label>
                <div style={{
                  border: `1px solid ${WS_COLOR.border}`,
                  borderRadius: WS_RADIUS.field,
                  padding: '16px',
                  backgroundColor: modalMode === 'view' ? WS_COLOR.surfaceSubtle : WS_COLOR.surface
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', cursor: modalMode !== 'view' ? 'pointer' : 'default' }}>
                    <input
                      type="radio"
                      name="dependency_type"
                      value="finish_to_start"
                      checked={formData.dependency_type === 'finish_to_start'}
                      onChange={() => setFormData(prev => ({ ...prev, dependency_type: 'finish_to_start' }))}
                      disabled={modalMode === 'view'}
                      style={{ marginTop: '4px', accentColor: WS_COLOR.primary }}
                    />
                    <div>
                      <div style={{ ...wsType('label'), color: WS_COLOR.text }}>Sequential (Finish-to-Start)</div>
                      <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: '4px' }}>
                        Source application must complete before target application starts
                      </div>
                    </div>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: modalMode !== 'view' ? 'pointer' : 'default' }}>
                    <input
                      type="radio"
                      name="dependency_type"
                      value="phase_based"
                      checked={formData.dependency_type === 'phase_based'}
                      onChange={() => setFormData(prev => ({ ...prev, dependency_type: 'phase_based' }))}
                      disabled={modalMode === 'view'}
                      style={{ marginTop: '4px', accentColor: WS_COLOR.primary }}
                    />
                    <div>
                      <div style={{ ...wsType('label'), color: WS_COLOR.text }}>Overlapping Phases</div>
                      <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: '4px' }}>
                        Both applications can run in parallel; target starts at specified source phase
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Dependency Type Description */}
              <div style={{
                ...wsTone('info'),
                padding: '12px 16px',
                borderRadius: WS_RADIUS.field,
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon={icons.ArrowRight} size={16} />
                <span style={wsType('table')}>
                  {formData.dependency_type === 'finish_to_start'
                    ? 'The target application will start immediately after the source application completes.'
                    : 'The target application will start when the source application reaches the specified phase.'}
                </span>
              </div>

              {/* Phase Selectors (only for phase_based) */}
              {formData.dependency_type === 'phase_based' && (
                <>
                  {/* Source Phase */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={FIELD_LABEL}>
                      Source Phase At Which Target Can Start *
                    </label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '4px' }}>
                      {PHASES.map((phase, index) => (
                        <button
                          key={phase}
                          type="button"
                          onClick={() => modalMode !== 'view' && setFormData(prev => ({ ...prev, source_phase_name: phase }))}
                          disabled={modalMode === 'view'}
                          style={{
                            ...wsType('meta'),
                            fontWeight: 500,
                            flex: 1,
                            padding: '10px 4px',
                            border: 'none',
                            backgroundColor: formData.source_phase_name === phase ? WS_COLOR.primary : WS_COLOR.tonal,
                            color: formData.source_phase_name === phase ? WS_COLOR.onPrimary : WS_COLOR.onTonal,
                            cursor: modalMode !== 'view' ? 'pointer' : 'default',
                            borderRadius: segmentRadius(index),
                            transition: 'all 0.2s'
                          }}
                        >
                          {phase.split(' ')[0]}
                        </button>
                      ))}
                    </div>
                    <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
                      Selected: {formData.source_phase_name}
                    </div>
                  </div>

                  {/* Target Phase (locked) */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={FIELD_LABEL}>
                      Target Application Starting Phase (Locked)
                    </label>
                    <div style={{ display: 'flex', gap: '2px', marginBottom: '4px', opacity: 0.6 }}>
                      {PHASES.map((phase, index) => (
                        <div
                          key={phase}
                          style={{
                            ...wsType('meta'),
                            fontWeight: 500,
                            flex: 1,
                            padding: '10px 4px',
                            // DW: the locked start phase is a neutral pair; the others are the tonal surface.
                            ...(phase === 'Assessment & Planning'
                              ? wsTone('neutral')
                              : { background: WS_COLOR.tonal, color: WS_COLOR.textMuted }),
                            textAlign: 'center',
                            borderRadius: segmentRadius(index)
                          }}
                        >
                          {phase.split(' ')[0]}
                        </div>
                      ))}
                    </div>
                    <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
                      Always starts at Assessment & Planning phase
                    </div>
                  </div>
                </>
              )}

              {/* Notes */}
              <div style={{ marginBottom: '24px' }}>
                <label style={FIELD_LABEL}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value.slice(0, 500) }))}
                  placeholder="Add any additional notes about this dependency..."
                  disabled={modalMode === 'view'}
                  rows={3}
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    backgroundColor: modalMode === 'view' ? WS_COLOR.surfaceSubtle : WS_COLOR.surface
                  }}
                />
                <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, textAlign: 'right', marginTop: '4px' }}>
                  {formData.notes.length}/500
                </div>
              </div>
            </form>
      </Sheet>
      {dialogs}
    </div>
  );
};

// DW: shared inline shapes for the form's custom dropdowns — kit field outline, label type and menu shadow.
const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  ...wsType('label'),
  color: WS_COLOR.text,
  marginBottom: '8px'
};

// DW: `.custom-dropdown` itself is drawn as the field by pp-workspace.css; the trigger fills it.
const TRIGGER: React.CSSProperties = {
  height: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const MENU: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  backgroundColor: WS_COLOR.surface,
  border: `1px solid ${WS_COLOR.border}`,
  borderRadius: WS_RADIUS.field,
  boxShadow: WS_COLOR.menuShadow,
  zIndex: 1000,
  maxHeight: '200px',
  overflowY: 'auto'
};

const OPTION: React.CSSProperties = {
  ...wsType('table'),
  padding: '12px 16px',
  cursor: 'pointer',
  borderBottom: `1px solid ${WS_COLOR.border}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

/** Segmented phase bar: rounded outer ends only. */
const segmentRadius = (index: number) =>
  index === 0 ? `${WS_RADIUS.field}px 0 0 ${WS_RADIUS.field}px` : index === PHASES.length - 1 ? `0 ${WS_RADIUS.field}px ${WS_RADIUS.field}px 0` : '0';

export default ApplicationDependencies;
